import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Calendar, ChevronDown, Clock, Search, SlidersHorizontal, User } from "lucide-react";
import type { Car as CarType, CarFilters } from "@/types/database";
import { defaultCarFilters, filterCars, uniqueValues, userDisplayName } from "@/lib/utils";
import { useUsers } from "@/lib/queries";
import { CarCard } from "@/components/cars/CarCard";
import { CarsExplorerAnalytics } from "@/components/cars/CarsExplorerAnalytics";
import { Input, Select } from "@/components/ui/Input";
import { Workspace, WorkspaceSection, FieldLabel } from "@/components/ui/Workspace";
import { ChipGroup } from "@/components/ui/FilterPanel";
import { cn } from "@/lib/cn";

const filterRevealEase = [0.16, 1, 0.3, 1] as const;

function countActiveFilters(filters: CarFilters): number {
  let count = 0;
  if (filters.search.trim()) count++;
  if (filters.yearMin.trim()) count++;
  if (filters.yearMax.trim()) count++;
  if (filters.transmission) count++;
  if (filters.body) count++;
  if (filters.make) count++;
  if (filters.model) count++;
  if (filters.userID) count++;
  return count;
}

export function CarsExplorer({ cars }: { cars: CarType[] }) {
  const [filters, setFilters] = useState<CarFilters>(defaultCarFilters());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { data: users = [] } = useUsers();

  const makes = useMemo(() => uniqueValues(cars, (c) => c.make), [cars]);
  const bodies = useMemo(() => uniqueValues(cars, (c) => c.body), [cars]);
  const models = useMemo(() => {
    const source = filters.make ? cars.filter((c) => c.make === filters.make) : cars;
    return uniqueValues(source, (c) => c.model);
  }, [cars, filters.make]);

  const usersById = useMemo(() => {
    const map = new Map<string, (typeof users)[number]>();
    for (const user of users) {
      if (user.userID) map.set(user.userID.toUpperCase(), user);
    }
    return map;
  }, [users]);

  const ownerOptions = useMemo(() => {
    return uniqueValues(cars, (c) => c.userID)
      .map((userID) => {
        const user = usersById.get(userID.toUpperCase());
        return {
          value: userID,
          label: user ? userDisplayName(user) : userID,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [cars, usersById]);

  const filtered = useMemo(() => filterCars(cars, filters), [cars, filters]);
  const activeFilters = countActiveFilters(filters);

  function resetAll() {
    setFilters(defaultCarFilters());
  }

  return (
    <Workspace>
      <WorkspaceSection className="border-b border-border/30 p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            className="inline-flex items-center gap-2 rounded-full bg-canvas px-4 py-2 text-sm font-bold text-foreground transition hover:bg-canvas-hover"
          >
            <SlidersHorizontal className="h-4 w-4" />
            {filtersOpen ? "Hide filters" : "Show filters"}
            {activeFilters > 0 ? (
              <span className="rounded-full bg-accent px-2 py-0.5 text-[11px] font-bold text-accent-foreground">
                {activeFilters}
              </span>
            ) : null}
            <ChevronDown
              className={cn("h-4 w-4 text-muted transition-transform duration-300", filtersOpen && "rotate-180")}
            />
          </button>

          {filtersOpen && activeFilters > 0 ? (
            <button
              type="button"
              onClick={resetAll}
              className="text-xs font-bold text-accent-link transition hover:text-foreground"
            >
              Reset all
            </button>
          ) : null}
        </div>
      </WorkspaceSection>

      <AnimatePresence initial={false}>
        {filtersOpen ? (
          <motion.div
            key="car-filters"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: filterRevealEase }}
            className="overflow-hidden"
          >
            <WorkspaceSection border className="p-4 sm:p-5">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="sm:col-span-2">
                  <FieldLabel icon={<Search className="h-3 w-3" />}>Search</FieldLabel>
                  <Input
                    placeholder="Make, model, plate…"
                    value={filters.search}
                    onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                  />
                </div>
                <div>
                  <FieldLabel icon={<Calendar className="h-3 w-3" />}>Year from</FieldLabel>
                  <Input
                    type="number"
                    placeholder="2010"
                    value={filters.yearMin}
                    onChange={(e) => setFilters((f) => ({ ...f, yearMin: e.target.value }))}
                  />
                </div>
                <div>
                  <FieldLabel icon={<Calendar className="h-3 w-3" />}>Year to</FieldLabel>
                  <Input
                    type="number"
                    placeholder="2026"
                    value={filters.yearMax}
                    onChange={(e) => setFilters((f) => ({ ...f, yearMax: e.target.value }))}
                  />
                </div>
                <div>
                  <FieldLabel icon={<Clock className="h-3 w-3" />}>Transmission</FieldLabel>
                  <Select
                    value={filters.transmission}
                    onChange={(e) => setFilters((f) => ({ ...f, transmission: e.target.value }))}
                  >
                    <option value="">All types</option>
                    <option value="Automatic">Automatic</option>
                  </Select>
                </div>
              </div>

              <div className="mt-6 space-y-6 border-t border-border/30 pt-6">
                {bodies.length > 0 ? (
                  <ChipGroup
                    label="Type"
                    options={bodies}
                    value={filters.body}
                    onChange={(body) => setFilters((f) => ({ ...f, body }))}
                  />
                ) : null}

                <div className="grid gap-3 sm:grid-cols-3">
                  <div>
                    <FieldLabel>Make</FieldLabel>
                    <Select
                      value={filters.make}
                      onChange={(e) =>
                        setFilters((f) => ({
                          ...f,
                          make: e.target.value,
                          model: e.target.value === f.make ? f.model : "",
                        }))
                      }
                    >
                      <option value="">All makes</option>
                      {makes.map((make) => (
                        <option key={make} value={make}>
                          {make}
                        </option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <FieldLabel>Model</FieldLabel>
                    <Select
                      value={filters.model}
                      onChange={(e) => setFilters((f) => ({ ...f, model: e.target.value }))}
                      disabled={models.length === 0}
                    >
                      <option value="">All models</option>
                      {models.map((model) => (
                        <option key={model} value={model}>
                          {model}
                        </option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <FieldLabel icon={<User className="h-3 w-3" />}>User</FieldLabel>
                    <Select
                      value={filters.userID}
                      onChange={(e) => setFilters((f) => ({ ...f, userID: e.target.value }))}
                    >
                      <option value="">All users</option>
                      {ownerOptions.map((owner) => (
                        <option key={owner.value} value={owner.value}>
                          {owner.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
              </div>
            </WorkspaceSection>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <WorkspaceSection border className="p-4 sm:p-5">
        <CarsExplorerAnalytics cars={filtered} />

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <p className="font-bold">No matches</p>
            <p className="mt-1 text-sm text-muted">Adjust your filters to see results.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
            {filtered.map((car) => (
              <CarCard key={car.carID} car={car} />
            ))}
          </div>
        )}
      </WorkspaceSection>
    </Workspace>
  );
}
